import { createSlice } from '@reduxjs/toolkit';
import createFetchReducer from '@app/utils/createFetchReducer';

import { GenericEntityState, ResponseAction } from '@app/redux/types';
import {
	createInquiryFormTemplateTypes,
	InquiryFormTemplateType,
	updateInquiryFormTemplateTypes,
	getInquiryFormTemplatesTypes,
	deleteInquiryFormTemplateTypes,
} from './types';
import { FIRST_PAGE } from '../constants';

export const initialState: GenericEntityState = {
	loading: false,
	error: {},
	response: {
		status: null,
		message: null,
	},
	pagination: {
		totalPages: null,
		currentPage: null,
		count: null,
		pages: {},
	},
	inquiryFormTemplatesCount: 0,
	forms: [],
	custom: {
		createInquiryFormTemplateLoading: false,
		updateInquiryFormTemplateLoading: false,
		deleteInquiryFormTemplateLoading: false,
	},
};

export type inquiryFormTemplatesType = typeof initialState;

const createInquiryFormTemplatesMapper = (
	state: inquiryFormTemplatesType,
	action: ResponseAction,
) => {
	state.forms = [action.response.inquiryFormTemplate, ...state.forms];
};

const updateInquiryFormTemplatesMapper = (
	state: inquiryFormTemplatesType,
	action: ResponseAction,
) => {
	const updatedInquiryFormTemplate = action.response.form;
	const inquiryFormTemplates = state.forms.map(
		(template: InquiryFormTemplateType) => {
			if (template.id === updatedInquiryFormTemplate.id) {
				return {
					...updatedInquiryFormTemplate,
					createdBy: template.createdBy,
					updatedBy: template.updatedBy,
				};
			}
			return template;
		},
	);
	state.forms = inquiryFormTemplates;
};

const getInquiryFormTemplatesMapper = (
	state: inquiryFormTemplatesType,
	action: ResponseAction,
) => {
	const page = action.response?.page || FIRST_PAGE;

	if (page === FIRST_PAGE) {
		state.forms = action.response.forms;
	} else {
		state.forms = [...state.forms, ...action.response.forms];
	}

	state.inquiryFormTemplatesCount = state.forms.length || 0;
	state.pagination = {
		totalPages: action.response.totalPages,
		currentPage: page,
		count: action.response.count,
		pages: {},
	};
};

const inquiryFormTemplateSlice = createSlice({
	name: 'inquiryFormTemplate',
	initialState,
	reducers: {
		RESET_INQUIRY_FORM_TEMPLATE_LIST(state: inquiryFormTemplatesType) {
			state.pagination = {
				pages: {},
				currentPage: null,
				totalPages: null,
			};

			state.forms = [];
			state.custom = {
				createInquiryFormTemplateLoading: false,
				updateInquiryFormTemplateLoading: false,
				deleteInquiryFormTemplateLoading: false,
				getInquiryFormTemplatesLoading: false,
			};
		},
	},
	extraReducers: {
		...createFetchReducer(
			createInquiryFormTemplateTypes,
			createInquiryFormTemplatesMapper,
		),
		...createFetchReducer(
			updateInquiryFormTemplateTypes,
			updateInquiryFormTemplatesMapper,
		),
		...createFetchReducer(
			getInquiryFormTemplatesTypes,
			getInquiryFormTemplatesMapper,
		),
		...createFetchReducer(deleteInquiryFormTemplateTypes),
	},
});

export default inquiryFormTemplateSlice.reducer;
